import { MessagesType } from '@telialt/common-cms/src/model';
import {
    SEARCH_FILTERABLE_FIELDS,
    SEARCH_PAGE_QUERY_PARAM,
    SORT_DROPDOWN_OPTIONS,
    SortOrder,
    URL_SORT_PARAM,
} from '@telialt/core-search/src/constants';
import { SearchFilters } from '@telialt/core-search/src/model';
import { splitByMainCommas } from '@telialt/core-search/src/utils/filtersUtils';

// Type for params in server side and client side
type SearchParams = Record<string, string> | URLSearchParams;

const getParamValue = (searchParams: SearchParams, name: string) => {
    if (typeof searchParams?.get === 'function') {
        return searchParams?.get(name);
    } else {
        return searchParams?.[name];
    }
};

export const readCategoryPageNumber = (searchParams: SearchParams): number => {
    const paramValue = getParamValue(searchParams, SEARCH_PAGE_QUERY_PARAM);

    return parseInt(paramValue, 10) || 1;
};

export const readCategorySortParam = (searchParams: SearchParams): string => {
    return getParamValue(searchParams, URL_SORT_PARAM);
};

export const readCategorySortFieldsList = (
    searchParams: SearchParams,
    t: MessagesType,
): Record<string, SortOrder>[] => {
    const paramValue = readCategorySortParam(searchParams);
    const sortFields = SORT_DROPDOWN_OPTIONS.find((option) => t[option.LABEL] === paramValue)?.SORT_ARRAY;

    return sortFields;
};

const isNumberValid = (value: string) => !!value && !isNaN(parseFloat(value));

export const readCategoryFilters = (
    searchParams: SearchParams,
    t: MessagesType,
    hasMessage: (key: string) => boolean,
): SearchFilters => {
    return Object.keys(SEARCH_FILTERABLE_FIELDS).reduce<SearchFilters>((acc, fieldName) => {
        const filterType = SEARCH_FILTERABLE_FIELDS[fieldName];
        const paramName = hasMessage(`eshop.category.facets.filter_url_label.${fieldName}`)
            ? t[`eshop.category.facets.filter_url_label.${fieldName}`]
            : fieldName;

        const rawParamValue = getParamValue(searchParams, paramName);

        if (!rawParamValue) {
            return acc;
        }

        if (filterType === 'hierarchy') {
            const terms = splitByMainCommas(rawParamValue);

            return { ...acc, [fieldName]: { level: 0, terms } };
        } else if (filterType === 'terms') {
            const terms = splitByMainCommas(rawParamValue);

            return { ...acc, [fieldName]: terms };
        } else if (filterType === 'range' || filterType === 'stats') {
            const splitParts = splitByMainCommas(rawParamValue);

            if (splitParts.length !== 2 || !isNumberValid(splitParts[0]) || !isNumberValid(splitParts[1])) {
                return acc;
            }

            return { ...acc, [fieldName]: { gte: parseFloat(splitParts[0]), lte: parseFloat(splitParts[1]) } };
        }

        return acc;
    }, {});
};
